import { MetarFogForecastResponse } from '../services/MetarFogForecastService';
import { ForecastTimelineData, ForecastTimelineSegment, ErrorInfo } from '../models/ForecastTimelineData';
import { HourlyForecastTimelineData, HourlyForecastTimelineSegment, HourlyActualsResponse, HourlyBucketResponse, HourlyFogActual, HourlyFogPrediction } from '../models/HourlyForecastTimelineData';
import { NZAirports } from '../types/Locations';
import { ALL_AIRPORTS_DATA } from '../data/locations';
import { parseOperatingHours, OperatingHoursInfo } from './operatingHours';

// Extended timeline data interface
interface ExtendedTimelineData extends ForecastTimelineData {
  errors?: ErrorInfo[];
  operatingHours?: OperatingHoursInfo;
}

// Extended hourly timeline data interface for METAR adapter
interface ExtendedHourlyTimelineData extends HourlyForecastTimelineData {
  errors?: ErrorInfo[];
  operatingHours?: OperatingHoursInfo;
}

/**
 * Maps the raw forecast data into a timeline-friendly format
 * @param forecast The raw forecast data from the API
 * @param locationId The location ID
 * @param locationName The location name/code
 * @returns A timeline-friendly data structure
 */
export function mapForecastToTimelineData(
  forecast: MetarFogForecastResponse,
  locationId: number,
  locationName: string
): ExtendedTimelineData {
  const timelineSegments: ForecastTimelineSegment[] = [];
  
  // Process actuals - these are already in the correct time segments
  forecast.aggregated_actuals.forEach(actual => {
    timelineSegments.push({
      time: new Date(actual.segment_time),
      fog_predicted: actual.fog_predicted,
      visibility_level: actual.visibility_median,
      lowest_visibility_level: actual.lowest_visibility_level ?? null,
      highest_visibility_level: actual.highest_visibility_level ?? null,
      raw_actuals: actual,
      raw_predictions: null,
      visibility_mean: actual.visibility_mean
    });
  });
  
  // Process predictions - use 30-minute predictions for METAR data
  forecast.aggregated_predictions_30min.forEach(prediction => {
    timelineSegments.push({
      time: new Date(prediction.segment_time),
      fog_predicted: prediction.fog_predicted,
      visibility_level: prediction.lowest_visibility_level,
      lowest_visibility_level: null,
      highest_visibility_level: null,
      raw_actuals: null,
      raw_predictions: prediction,
      visibility_mean: null
    });
  });
  
  // Ensure segments are sorted by time
  const sortedSegments = timelineSegments.sort(
    (a, b) => a.time.getTime() - b.time.getTime()
  );
  
  // Create the timeline data with errors from the forecast
  // METAR data uses 30-minute prediction segments
  const result: ExtendedTimelineData = {
    location_id: locationId,
    location_name: locationName,
    segments: sortedSegments,
    segment_interval: 30
  };

  applyErrorsAndOperatingHours(result, forecast.errors);

  return result;
}

/**
 * Extracts errors and operating hours from forecast errors onto a result object.
 */
function applyErrorsAndOperatingHours(
  result: { errors?: ErrorInfo[]; operatingHours?: OperatingHoursInfo },
  errors?: ErrorInfo[]
): void {
  if (!errors || errors.length === 0) return;

  result.errors = errors;

  const outOfHoursError = errors.find(e => e.code === 'OUT_OF_OPERATING_HOURS');
  if (outOfHoursError?.details?.operating_hours) {
    const operatingHours = parseOperatingHours(outOfHoursError.details.operating_hours as string);
    if (operatingHours) {
      result.operatingHours = operatingHours;
    }
  }
}

/**
 * Gets the airport name for a location ID
 * @param locationId The location ID
 * @returns The airport name (e.g., "Auckland") or ICAO code as fallback
 */
export function getAirportName(locationId: number): string {
  const airport = ALL_AIRPORTS_DATA.find(a => a.id === locationId);
  if (airport) {
    return airport.name;
  }
  // Fallback to ICAO code if not found in data
  return NZAirports[locationId] || `Unknown (${locationId})`;
}

/**
 * Maps forecasts for multiple locations into timeline data
 */
export function mapMultipleForecasts(
  forecasts: Array<{ locationId: number, forecast: MetarFogForecastResponse }>
): ExtendedTimelineData[] {
  return forecasts.map(item => {
    const locationId = item.locationId;
    const locationName = getAirportName(locationId);

    return mapForecastToTimelineData(
      item.forecast,
      locationId,
      locationName
    );
  });
}

/**
 * Maps METAR forecast data into HourlyForecastTimelineData format.
 * Adapts 30-min actuals + 60-min hourly predictions from the METAR response
 * into the shape consumed by HourlyForecastTimeline (which handles mixed intervals).
 */
export function mapForecastToHourlyTimelineData(
  forecast: MetarFogForecastResponse,
  locationId: number,
  locationName: string
): ExtendedHourlyTimelineData {
  const timelineSegments: HourlyForecastTimelineSegment[] = [];

  // Map actuals → HourlyForecastTimelineSegment with raw_actuals as HourlyActualsResponse
  forecast.aggregated_actuals.forEach(actual => {
    const bucketStart = actual.segment_time;
    const bucketEnd = new Date(new Date(bucketStart).getTime() + 30 * 60 * 1000).toISOString();

    const adaptedActuals: HourlyActualsResponse = {
      location: actual.location,
      bucket_start: bucketStart,
      bucket_end: bucketEnd,
      fog_actuals: actual.fog_actuals as unknown as HourlyFogActual[],
      fog_predicted: actual.fog_predicted,
      visibility_hmean: actual.visibility_hmean,
      visibility_median: actual.visibility_median,
      visibility_mean: actual.visibility_mean,
      lowest_visibility_level: actual.lowest_visibility_level ?? null,
      highest_visibility_level: actual.highest_visibility_level ?? null,
      actual_count: actual.fog_actuals.length
    };

    timelineSegments.push({
      time: new Date(bucketStart),
      fog_predicted: actual.fog_predicted,
      visibility_level: actual.visibility_hmean,
      lowest_visibility_level: actual.lowest_visibility_level ?? null,
      highest_visibility_level: actual.highest_visibility_level ?? null,
      raw_actuals: adaptedActuals,
      raw_predictions: null,
      visibility_mean: actual.visibility_mean
    });
  });

  // Map hourly predictions → HourlyForecastTimelineSegment with raw_predictions as HourlyBucketResponse
  forecast.aggregated_predictions_hourly.forEach(prediction => {
    const bucketStart = prediction.segment_time;
    const bucketEnd = new Date(new Date(bucketStart).getTime() + 60 * 60 * 1000).toISOString();

    const adaptedPredictions: HourlyBucketResponse = {
      location: prediction.location,
      horizon: prediction.horizon,
      bucket_start: bucketStart,
      bucket_end: bucketEnd,
      predictions: prediction.predictions as unknown as HourlyFogPrediction[],
      fog_predicted: prediction.fog_predicted,
      confidence: 0,
      normalized_confidence: prediction.normalized_confidence ?? null,
      lowest_visibility_level: prediction.lowest_visibility_level,
      threshold_results: [],
      trend: 'neutral',
      trend_slope: null,
      prediction_count: prediction.predictions.length,
      evaluation_count: 0
    };

    timelineSegments.push({
      time: new Date(bucketStart),
      fog_predicted: prediction.fog_predicted,
      visibility_level: prediction.lowest_visibility_level,
      lowest_visibility_level: null,
      highest_visibility_level: null,
      raw_actuals: null,
      raw_predictions: adaptedPredictions,
      visibility_mean: null
    });
  });

  // Sort by time
  const sortedSegments = timelineSegments.sort(
    (a, b) => a.time.getTime() - b.time.getTime()
  );

  const result: ExtendedHourlyTimelineData = {
    location_id: locationId,
    location_name: locationName,
    segments: sortedSegments,
    segment_interval: 30
  };

  applyErrorsAndOperatingHours(result, forecast.errors);

  return result;
}
